import * as signalR from '@microsoft/signalr';

export interface InventoryDataUpdatedEvent {
  orderId: number;
  trigger: string;
}

export type InventoryDataUpdatedCallback = (event: InventoryDataUpdatedEvent) => void;

class InventorySignalRService {
  private connection: signalR.HubConnection | null = null;
  private callbacks: Set<InventoryDataUpdatedCallback> = new Set();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Initialize the SignalR connection to the update hub
   */
  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl('/updateHub')
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.connection.onreconnecting((error) => {
        console.log('[InventorySignalR] Reconnecting...', error?.message);
      });

      this.connection.onreconnected((connectionId) => {
        console.log('[InventorySignalR] Reconnected:', connectionId);
        this.reconnectAttempts = 0;
      });

      this.connection.onclose((error) => {
        console.log('[InventorySignalR] Connection closed:', error?.message);
        this.handleConnectionClosed();
      });

      this.connection.on('InventoryDataUpdated', (event: InventoryDataUpdatedEvent) => {
        console.log('[InventorySignalR] Received event:', event);
        this.notifyCallbacks(event);
      });

      await this.connection.start();
      console.log('[InventorySignalR] Connected successfully');
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('[InventorySignalR] Connection failed:', error);
      this.handleConnectionClosed();
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Handle connection closure and attempt reconnection
   */
  private handleConnectionClosed(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.callbacks.size > 0) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      console.log(`[InventorySignalR] Will attempt reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
      setTimeout(() => this.connect(), delay);
    }
  }

  /**
   * Subscribe to inventory data updated events
   * @param callback Function to call when an event is received
   * @returns Unsubscribe function
   */
  subscribe(callback: InventoryDataUpdatedCallback): () => void {
    this.callbacks.add(callback);

    if (this.connection?.state !== signalR.HubConnectionState.Connected && !this.isConnecting) {
      this.connect();
    }

    return () => {
      this.callbacks.delete(callback);

      if (this.callbacks.size === 0) {
        this.disconnect();
      }
    };
  }

  /**
   * Notify all callbacks of an event
   */
  private notifyCallbacks(event: InventoryDataUpdatedEvent): void {
    this.callbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('[InventorySignalR] Callback error:', error);
      }
    });
  }

  /**
   * Disconnect from the SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('[InventorySignalR] Disconnected');
      } catch (error) {
        console.error('[InventorySignalR] Disconnect error:', error);
      }
      this.connection = null;
    }
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Singleton instance
let serviceInstance: InventorySignalRService | null = null;

export function getInventorySignalR(): InventorySignalRService {
  if (!serviceInstance) {
    serviceInstance = new InventorySignalRService();
  }
  return serviceInstance;
}

export default InventorySignalRService;
